/**
 * AssessmentDocument Component
 *
 * Read-only, document-style view of an assessment. Typography-first, card-based layout.
 *
 * Sections:
 * - DocumentHeader (name, status, branding)
 * - PlanSection (scenario, learner experience)
 * - CompetencySection (weights, visual hierarchy)
 * - PersonaCard
 * - LearnerBriefingSection
 * - AssessmentDetailsSection (duration, type, scoring)
 * - DocumentFooter (date, version)
 *
 * Designed for print: uses @media print styles and window.print() for PDF export.
 */

import { PersonaCard } from './PersonaCard';
import { StatusBadge } from './RequestPanel';
import type { Assessment, AssessmentPackageResponse } from '../lib/types';

interface AssessmentDocumentProps {
  assessment: Assessment;
  package_: AssessmentPackageResponse;
  onPrint?: () => void;
  onPreviewAsLearner?: () => void;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3
      style={{
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--text-primary)',
        margin: '0 0 12px 0',
        paddingBottom: 8,
        borderBottom: '1px solid var(--border-default)',
      }}
    >
      {children}
    </h3>
  );
}

function WeightBar({ weight }: { weight: number }) {
  const pct = Math.round(weight * 100);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div
        style={{
          flex: 1,
          height: 6,
          background: 'var(--slate-100)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--sky-500)',
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text-secondary)',
          minWidth: 40,
          textAlign: 'right',
        }}
      >
        {pct}%
      </span>
    </div>
  );
}

export function AssessmentDocument({
  assessment,
  package_,
  onPrint,
  onPreviewAsLearner,
}: AssessmentDocumentProps) {
  const name = (assessment.metadata?.['name'] as string) || 'Untitled Assessment';
  const objective = (assessment.metadata?.['objective'] as string) || '';
  const createdDate = new Date(assessment.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div
      className="assessment-document"
      style={{
        maxWidth: 800,
        margin: '0 auto',
        fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      {/* Action bar (hidden in print) */}
      <div
        className="no-print"
        style={{
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        {onPreviewAsLearner && (
          <button className="btn btn-secondary" onClick={onPreviewAsLearner}>
            Preview as Learner
          </button>
        )}
        {onPrint && (
          <button className="btn btn-secondary" onClick={onPrint}>
            Export PDF
          </button>
        )}
      </div>

      {/* Document Header */}
      <div
        style={{
          background: 'var(--slate-800)',
          color: 'white',
          borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
          padding: '32px 32px 24px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 6px 0', lineHeight: 1.2 }}>
              {name}
            </h1>
            <div style={{ fontSize: 14, color: 'var(--slate-300)' }}>
              Voice Assessment
              {assessment.assessmentMode === 'quiz' && ' (Quiz Mode)'}
              {assessment.assessmentType && (
                <> &middot; {assessment.assessmentType === 'pre' ? 'Pre-training' : 'Post-training'}</>
              )}
            </div>
          </div>
          <StatusBadge status={assessment.status} />
        </div>
        {objective && (
          <p style={{ fontSize: 14, color: 'var(--slate-300)', margin: 0, lineHeight: 1.6 }}>
            {objective}
          </p>
        )}
      </div>

      {/* Document body */}
      <div
        style={{
          border: '1px solid var(--border-default)',
          borderTop: 'none',
          borderRadius: '0 0 var(--radius-lg) var(--radius-lg)',
          background: 'white',
          padding: '24px 32px',
        }}
      >
        {/* Plan Section: Scenario */}
        <section style={{ marginBottom: 28 }}>
          <SectionHeading>Scenario</SectionHeading>
          <div
            style={{
              background: 'var(--slate-50)',
              borderRadius: 'var(--radius-md)',
              padding: 16,
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>
              {package_.scenario.title}
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px 0', lineHeight: 1.6 }}>
              {package_.scenario.context}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-secondary)',
                    marginBottom: 4,
                  }}
                >
                  Situation
                </div>
                <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  {package_.scenario.situation}
                </p>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-secondary)',
                    marginBottom: 4,
                  }}
                >
                  Learner Role
                </div>
                <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                  {package_.scenario.learnerRole}
                </p>
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'var(--text-secondary)',
                  marginBottom: 4,
                }}
              >
                Expected Outcome
              </div>
              <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
                {package_.scenario.expectedOutcome}
              </p>
            </div>
          </div>
        </section>

        {/* Competency Section */}
        <section style={{ marginBottom: 28 }}>
          <SectionHeading>Competencies ({package_.competencies.length})</SectionHeading>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {package_.competencies.map((comp) => (
              <div
                key={comp.id}
                style={{
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 6,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{comp.name}</div>
                    {comp.category && (
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--text-secondary)',
                          fontWeight: 500,
                        }}
                      >
                        {comp.category}
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      padding: '2px 8px',
                      borderRadius: 'var(--radius-sm)',
                      textTransform: 'uppercase',
                      background:
                        comp.scoringType === 'rubric'
                          ? 'var(--sky-50)'
                          : comp.scoringType === 'scale'
                            ? 'var(--amber-50)'
                            : 'var(--slate-100)',
                      color:
                        comp.scoringType === 'rubric'
                          ? 'var(--sky-600)'
                          : comp.scoringType === 'scale'
                            ? 'var(--amber-700)'
                            : 'var(--slate-600)',
                    }}
                  >
                    {comp.scoringType}
                  </span>
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: 'var(--text-secondary)',
                    margin: '0 0 8px 0',
                    lineHeight: 1.5,
                  }}
                >
                  {comp.description}
                </p>
                <WeightBar weight={comp.weight} />
              </div>
            ))}
          </div>
        </section>

        {/* Persona Section */}
        <section style={{ marginBottom: 28 }}>
          <SectionHeading>AI Persona</SectionHeading>
          <PersonaCard persona={package_.persona} />
        </section>

        {/* Learner Briefing */}
        <section style={{ marginBottom: 28 }}>
          <SectionHeading>Learner Briefing</SectionHeading>
          <div
            style={{
              background: 'var(--sky-50)',
              borderRadius: 'var(--radius-md)',
              padding: 16,
              border: '1px solid var(--sky-200)',
            }}
          >
            <p style={{ fontSize: 13, margin: '0 0 8px 0', lineHeight: 1.6, fontWeight: 500 }}>
              You will be speaking with <strong>{package_.persona.name}</strong>, who is a{' '}
              <strong>{package_.persona.role}</strong>.
            </p>
            <p style={{ fontSize: 13, margin: '0 0 8px 0', lineHeight: 1.6 }}>
              <strong>Scenario:</strong> {package_.scenario.situation}
            </p>
            <p style={{ fontSize: 13, margin: '0 0 8px 0', lineHeight: 1.6 }}>
              <strong>Your role:</strong> {package_.scenario.learnerRole}
            </p>
            <p style={{ fontSize: 13, margin: 0, lineHeight: 1.6 }}>
              <strong>Your goal:</strong> {package_.scenario.expectedOutcome}
            </p>
          </div>
        </section>

        {/* Assessment Details */}
        <section style={{ marginBottom: 28 }}>
          <SectionHeading>Assessment Details</SectionHeading>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
            <DetailRow
              label="Assessment Mode"
              value={assessment.assessmentMode === 'quiz' ? 'Quiz' : 'Voice Conversation'}
            />
            <DetailRow
              label="Assessment Timing"
              value={
                assessment.assessmentType === 'pre'
                  ? 'Pre-training'
                  : assessment.assessmentType === 'post'
                    ? 'Post-training'
                    : 'Not specified'
              }
            />
            <DetailRow
              label="Scoring Method"
              value={`${package_.rubric.criteria.length} criteria, max score ${package_.rubric.maxScore}`}
            />
            <DetailRow
              label="Passing Score"
              value={`${package_.rubric.passingScore} / ${package_.rubric.maxScore}`}
            />
            <DetailRow
              label="Competencies"
              value={`${package_.competencies.length} competencies`}
            />
            <DetailRow
              label="Status"
              value={assessment.status.charAt(0).toUpperCase() + assessment.status.slice(1)}
            />
          </div>
        </section>

        {/* Document Footer */}
        <footer
          style={{
            paddingTop: 16,
            borderTop: '1px solid var(--border-default)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          <div>
            Created {createdDate} &middot; Package v{package_.version}
          </div>
          <div style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {assessment.id.slice(0, 12)}...
          </div>
        </footer>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .assessment-document {
            max-width: 100% !important;
            margin: 0 !important;
          }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--text-secondary)',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14 }}>{value}</div>
    </div>
  );
}
